// RPC cache policy table.
//
// Each entry maps an RPC method name to the IndexedDB store + key
// derivation it should use. The wrapped rpc() in rpc.ts consults this
// table for every call:
//   • methods without a policy → passthrough (network only)
//   • `read` methods → network-first, fall back to cache on network
//     failure, write-through on success
//   • `write` methods → Phase C (queue + optimistic apply)
//
// The cached value is the FULL raw RPC response (e.g. `{ ok: true,
// items: [...] }`) so the api method's downstream `.items` access
// works identically whether the value came from network or cache.

export type CachePolicyKind = "read" | "write" | "passthrough";

export interface CachePolicy {
  kind: CachePolicyKind;
  /** IDB store this method's responses live in. Required for `read`
   *  policies; optional for `write` (which only writes to
   *  `pending_writes`). */
  store?: string;
  /** Derive a stable string key from the call's args. Used by `read`
   *  policies; not used by `write`. */
  key?: (args: Record<string, any>) => string;
  /** Write-policy only. Synthesize a usable response shape from the
   *  call's args when the request had to be queued (no network).
   *  Hooks that do `r.item` etc. need SOMETHING shaped like the
   *  real response — e.g. todos.updateItem synthesizes
   *  `{ ok: true, item: { id: itemId, ...patch } }`. The drainer
   *  later replaces the optimistic in-memory state with the
   *  authoritative server response. */
  synthesize?: (args: Record<string, any>) => Record<string, any>;
}

// Singleton key — for methods with no args / one global cache slot.
const ONE = () => "_";

/* eslint-disable @typescript-eslint/no-explicit-any */
export const CACHE_POLICIES: Record<string, CachePolicy> = {
  // ── Notes ──────────────────────────────────────────────────────
  "notes.listRoots":     { kind: "read", store: "notes_roots",     key: ONE },
  "notes.listDir":       { kind: "read", store: "notes_dirs",      key: (a) => `${a.root}:${a.path || ""}` },
  "notes.read":          { kind: "read", store: "notes_files",     key: (a) => `${a.root}:${a.path}` },
  "notes.listTemplates": { kind: "read", store: "notes_templates", key: (a) => a.root },

  // ── To-do ──────────────────────────────────────────────────────
  "todos.listLists": { kind: "read", store: "todos_lists", key: ONE },
  "todos.listItems": { kind: "read", store: "todos_items", key: (a) => String(a.listId) },

  // ── Shopping ───────────────────────────────────────────────────
  "shopping.listLists": { kind: "read", store: "shopping_lists", key: ONE },
  "shopping.listItems": { kind: "read", store: "shopping_items", key: (a) => String(a.listId) },

  // ── Calendar ───────────────────────────────────────────────────
  // Range-keyed: an offline user moving between adjacent months sees
  // each visited range cached separately. The wrapper falls back to
  // any-range cache only when the requested key misses (Phase C will
  // add cross-range fuzzy fallbacks if it ends up mattering).
  "calendar.listEvents":      { kind: "read", store: "calendar_events",  key: (a) => `${a.rangeStart}|${a.rangeEnd}` },
  "calendar.listMyCalendars": { kind: "read", store: "calendars",        key: ONE },
  "calendar.listMembers":     { kind: "read", store: "calendar_members", key: (a) => String(a.id) },

  // ── Meal plan ──────────────────────────────────────────────────
  "mealplan.listForRange": { kind: "read", store: "mealplan_entries", key: (a) => `${a.from}|${a.to}` },

  // ── Recipes ────────────────────────────────────────────────────
  // List + per-recipe-get are the two read paths. Cooking on the
  // subway is the use case — open the app, tap a recipe, get the
  // ingredients + steps even without bars. Auto-tags-on-import
  // refreshes the cached list via the SSE listener in useRecipes,
  // so the cache doesn't go stale for tag chips after a fresh save.
  "recipes.list": { kind: "read", store: "recipes_list", key: ONE },
  "recipes.get":  { kind: "read", store: "recipes_get",  key: (a) => String(a.recipeId) },

  // ── Pantry ─────────────────────────────────────────────────────
  // Single read RPC — pantry.list returns every field the UI needs
  // (qty, expires, lowStock, location, category). No detail RPC
  // because there's no row-level data outside the list shape. The
  // global ⌘K palette scans the same cache.
  "pantry.list": { kind: "read", store: "pantry_list", key: ONE },

  // ── Gallery / Studio ───────────────────────────────────────────
  // The hook only ever calls gallery.list({ limit: 60 }); for now
  // we collapse the cache to a single slot. If we later page through
  // older gens, this key will need offset+query.
  "gallery.list": { kind: "read", store: "gallery_items", key: (a) => `${a.query || ""}|${a.offset || 0}|${a.limit || 100}` },
  "gallery.get":  { kind: "read", store: "gallery_item",  key: (a) => String(a.id) },

  // ── Settings (image-gen prefs + options) ───────────────────────
  // These read endpoints rarely change; caching them lets the Studio
  // composer paint chips before the network round-trip lands.
  "imageGen.listOptions": { kind: "read", store: "meta", key: () => "imageGen.listOptions" },
  "imageGen.getSettings": { kind: "read", store: "meta", key: () => "imageGen.getSettings" },

  // ── Writes (Phase C: queue while offline, drain on reconnect) ──
  // Synthesize responses are pessimistic best-guesses. The drainer
  // replays the queued write on reconnect; the real server response
  // then reconciles state through the hooks' normal refresh cycle.
  //
  // Methods without a synthesize but with kind:"write" still queue —
  // they just return `{ ok: true, _queued: true }` (callers must
  // tolerate the missing payload fields).

  // To-do
  "todos.addItem":       { kind: "write", synthesize: (a) => ({
    ok: true,
    item: { id: `_pending:${randomKey()}`, listId: a.listId, title: a.title, done: false, ...a, _queued: true },
  }) },
  "todos.updateItem":    { kind: "write", synthesize: (a) => {
    // Caller passes { itemId, ...patch }. The api method spreads patch
    // into the args object — we echo it back as the "updated" item so
    // setItems(prev.map(...)) gets a shape with the new fields.
    const { itemId, ...patch } = a;
    return { ok: true, item: { id: itemId, ...patch, _queued: true } };
  } },
  "todos.deleteItem":    { kind: "write", synthesize: () => ({ ok: true, _queued: true }) },
  "todos.clearDone":     { kind: "write", synthesize: () => ({ ok: true, deleted: 0, _queued: true }) },

  // Shopping
  "shopping.addItem":         { kind: "write", synthesize: (a) => ({
    ok: true,
    item: { id: tempNumericId(), listId: a.listId, text: a.text, quantity: a.quantity || "",
            purchased: false, position: 0, category: "", emoji: "", _queued: true },
  }) },
  "shopping.updateItem":      { kind: "write", synthesize: (a) => {
    const { itemId, ...patch } = a;
    // Synthesize purchasedAt locally for offline toggles so the
    // "Got it" group's recency sort still surfaces the item to the
    // top. The drainer replays the write online; the server stamp
    // overwrites this on next sync.
    const stamped = patch.purchased === undefined
      ? patch
      : { ...patch, purchasedAt: patch.purchased ? new Date().toISOString() : null };
    return { ok: true, item: { id: itemId, ...stamped, _queued: true } };
  } },
  "shopping.deleteItem":      { kind: "write", synthesize: () => ({ ok: true, _queued: true }) },
  "shopping.clearPurchased":  { kind: "write", synthesize: () => ({ ok: true, deleted: 0, _queued: true }) },
  "shopping.createList":      { kind: "write", synthesize: (a) => ({
    ok: true, list: { id: tempNumericId(), name: a.name, isShared: !!a.isShared, archived: false, _queued: true },
  }) },
  "shopping.updateList":      { kind: "write", synthesize: (a) => ({ ok: true, list: { id: a.listId, ...a, _queued: true } }) },
  "shopping.deleteList":      { kind: "write", synthesize: () => ({ ok: true, _queued: true }) },

  // Meal plan
  "mealplan.set":   { kind: "write", synthesize: (a) => ({
    ok: true,
    entry: { id: `mp_pending_${randomKey()}`, date: a.date, slot: a.slot, value: a.value, recipeId: a.recipeId || null, _queued: true },
  }) },
  "mealplan.clear": { kind: "write", synthesize: () => ({ ok: true, removed: true, _queued: true }) },

  // Notes
  "notes.save":   { kind: "write", synthesize: (a) => ({
    // The save-state pill in the editor reads `sha256` + `mtime`;
    // returning a placeholder keeps the UI from breaking. On drain
    // the real server response (incl. conflict, if any) flows
    // through the existing conflict-detection path.
    ok: true,
    sha256: "_pending",
    size: typeof a.content === "string" ? a.content.length : 0,
    mtime: Date.now(),
    _queued: true,
  }) },
  "notes.delete": { kind: "write", synthesize: () => ({ ok: true, _queued: true }) },
  "notes.rename": { kind: "write", synthesize: (a) => ({
    ok: true, newPath: a.newPath, sha256: null, _queued: true,
  }) },

  // Calendar — only the simple writes for now. updateEvent is
  // intentionally omitted: the synthesizer would need to merge with
  // current state (drag-reschedule keeps duration etc.) and that
  // belongs in a follow-up pass alongside an optimistic local-cache
  // mutator.
  "calendar.createEvent": { kind: "write", synthesize: (a) => ({
    ok: true,
    event: {
      id: `evt_pending_${randomKey()}`,
      ownerUserId: "", calendarId: a.calendarId ?? null,
      masterId: null, originalDtstart: null,
      title: a.title, description: a.description || "", location: a.location || "",
      dtstart: a.dtstart, dtend: a.dtend, allDay: !!a.allDay,
      private: false, rrule: null, exdate: "", isRecurring: false,
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      _queued: true,
    },
  }) },
  "calendar.deleteEvent": { kind: "write", synthesize: (a) => ({ ok: true, id: a.id, _queued: true }) },

  // Recipes — every mutation queues offline + synthesizes a
  // shape the hook can fold into local state. Auto-tag classifier
  // and re-tag don't get synth payloads (they fire async server-
  // side and return tags via SSE, which won't fire offline anyway).
  "recipes.create":          { kind: "write", synthesize: (a) => ({
    ok: true,
    recipe: { id: tempNumericId(), name: a.name, imageUrl: null, instructions: null, notes: null, tags: null, serves: null, _queued: true },
  }) },
  "recipes.update":          { kind: "write", synthesize: (a) => {
    const { recipeId, ...patch } = a;
    return { ok: true, recipe: { id: recipeId, ...patch, _queued: true } };
  } },
  "recipes.delete":          { kind: "write", synthesize: () => ({ ok: true, _queued: true }) },
  "recipes.setTags":         { kind: "write", synthesize: (a) => ({
    ok: true, recipe: { id: a.recipeId, tags: a.tags || [], _queued: true },
  }) },
  "recipes.retag":           { kind: "write", synthesize: () => ({ ok: true, _queued: true }) },
  "recipes.addItem":         { kind: "write", synthesize: (a) => ({
    ok: true, item: { id: tempNumericId(), recipeId: a.recipeId, text: a.text, quantity: a.quantity || "", position: 0, category: null, emoji: null, _queued: true },
  }) },
  "recipes.updateItem":      { kind: "write", synthesize: (a) => {
    const { itemId, ...patch } = a;
    return { ok: true, item: { id: itemId, ...patch, _queued: true } };
  } },
  "recipes.deleteItem":      { kind: "write", synthesize: () => ({ ok: true, _queued: true }) },
  "recipes.createWithItems": { kind: "write", synthesize: (a) => ({
    ok: true,
    recipe: { id: tempNumericId(), name: a.name, imageUrl: a.imageUrl || null, instructions: a.instructions || null, notes: null, tags: null, serves: a.serves || null, _queued: true },
    itemCount: Array.isArray(a.items) ? a.items.length : 0,
  }) },
  "recipes.addToList":       { kind: "write", synthesize: (a) => ({
    ok: true, added: 0, skipped: 0, listId: a.listId, _queued: true,
  }) },
  // recipes.import is intentionally NOT a write here — it requires
  // online fetch + JSON-LD/LLM parsing of a remote URL or pasted
  // text. There's no useful synthesized payload; offline it fails
  // and the user retries when back online.

  // Pantry — three writes. expires synthesizes as null on the queued
  // path because inferExpiry only runs server-side; the real response
  // patches in the assigned date when the drain replays online.
  "pantry.add": { kind: "write", synthesize: (a) => ({
    ok: true,
    item: {
      id: tempNumericId(),
      name: a.name,
      qty: a.qty || "1",
      category: a.category || "Other",
      location: a.location || null,
      expires: a.expires === undefined ? null : a.expires,
      lowStock: !!a.lowStock,
      source: a.source || "manual",
      _queued: true,
    },
  }) },
  "pantry.update": { kind: "write", synthesize: (a) => {
    const { itemId, ...patch } = a;
    return { ok: true, item: { id: itemId, ...patch, _queued: true } };
  } },
  "pantry.delete": { kind: "write", synthesize: () => ({ ok: true, _queued: true }) },
};

// Temp id helpers. The `_pending:` prefix on string ids and the
// negative range for numeric ids let downstream code spot synthetic
// records (e.g. the drainer can refuse to delete a temp-id row;
// gallery filters can hide them).
function randomKey(): string {
  return Math.random().toString(36).slice(2, 10);
}
let _tempNumericIdCounter = -1;
function tempNumericId(): number {
  // Negative + decreasing so two offline-created items get distinct
  // ids without clashing with any real positive server id.
  return _tempNumericIdCounter--;
}

export function lookupPolicy(method: string): CachePolicy | null {
  return CACHE_POLICIES[method] || null;
}
